import type {
	PlatformInputSchema,
	PlatformOperationInputGateway,
	PlatformOperationInputState,
	PlatformOperationTypeDescriptor,
} from '@/platform/ui/contracts'
import { ApiService } from './api'

export type EventOwnerKind = 'platform' | 'instrument' | 'module' | 'space'
export type EventRuleStatus = 'enabled' | 'disabled'
export type EventPublishStatus = 'saved' | 'published'
export type EventConditionOperator = 'exists' | 'not_null' | 'type_is' | 'positive' | 'contains' | 'equals' | 'not_equals'
export type EventConditionGroupOperator = 'and' | 'or'
export type EventScopeKind = 'space' | 'entity' | 'platform'

export interface EventOwnerDto {
	kind: EventOwnerKind
	instrument_key?: string | null
	module_key?: string | null
	space_id?: number
}

export interface EventFilterFieldDto {
	path: string
	title: string
	value_type: string
	description?: string | null
	enum?: unknown[]
}

export interface EventTypeSummaryDto {
	event_space_id: number
	event_key: string
	title: string
	description?: string | null
	owner: EventOwnerDto
	status_key: string
	available: boolean
	filter_fields: EventFilterFieldDto[]
	descriptor_revision: number
}

export interface EventParentDto extends EventTypeSummaryDto {
	distance: number
	relation_kind_key: string
}

export interface EventTypeDetailDto extends EventTypeSummaryDto {
	parent_chain: EventParentDto[]
	guaranteed_values: Record<string, unknown>
	authoring_hints: Record<string, unknown>
	registry_revision: number
}

export interface EventConditionDto {
	field: string
	operator: EventConditionOperator
	value: unknown
}

export interface EventRuleDto {
	id: number
	space_id: number
	event_space_id: number
	event_key: string
	conditions_operator_key: EventConditionGroupOperator
	operation_input_id: number | null
	active_from: string | null
	active_until: string | null
	status_key: EventRuleStatus
	revision: number
	publish_status_key: EventPublishStatus
	published_revision: number | null
	management_owner_kind_key: EventOwnerKind
	management_owner_key: string | null
	created_at: string
	updated_at: string
	deleted: boolean
}

export interface EventEntityReferenceDto {
	entity_type: string
	entity_id: string
	title: string
	subtitle?: string | null
	status_key?: string | null
}

export interface EventScopeDto {
	id: number
	rule_id: number
	scope_kind_key: EventScopeKind
	entity_type: string | null
	entity_id: string | null
	revision: number
	publish_status_key: EventPublishStatus
	published_revision: number | null
	deleted: boolean
	entity?: EventEntityReferenceDto | null
}

export interface EventOperationInputDto {
	id: number
	space_id: number
	operation_type_key: string
	input: Record<string, unknown>
	revision: number
	descriptor_revision: number
	publish_status_key: EventPublishStatus
	published_revision: number | null
	deleted: boolean
}

export interface EventReactionConflictDto {
	rule_id?: number
	other_rule_id?: number
	code?: string
	message?: string
	[key: string]: unknown
}

export interface EventReactionPermissionsDto {
	can_view: boolean
	can_edit: boolean
	can_archive: boolean
	can_publish: boolean
}

export interface EventReactionListItemDto {
	rule: EventRuleDto
	event: EventTypeSummaryDto
	conditions: EventConditionDto[]
	scopes: EventScopeDto[]
	operation_input: EventOperationInputDto | null
	operation: {
		kind: 'operation' | 'stop'
		title: string
		operation_input_id?: number
		operation_type_key?: string
	}
	management_owner: { kind: EventOwnerKind; key: string | null; label: string }
	permissions: EventReactionPermissionsDto
	condition_specificity: number
	conflicts: EventReactionConflictDto[]
	publish_state: { status_key: EventPublishStatus; revision: number; published_revision: number | null }
	summary: string
}

export interface EventReactionDetailDto extends EventReactionListItemDto {
	event: EventTypeDetailDto
	operation_form: OperationFormContractDto | null
	space_timezone: string
}

export interface EventPaginationDto {
	page: number
	page_size: 20 | 50 | 100
	total: number
	pages: number
}

export interface EventReactionListDto {
	items: EventReactionListItemDto[]
	pagination: EventPaginationDto
	filter_values: {
		owner_kind: EventOwnerKind[]
		status_key: EventRuleStatus[]
		publish_status_key: EventPublishStatus[]
		has_conflict: boolean[]
	}
}

export interface EventReactionListQuery {
	page?: number
	page_size?: 20 | 50 | 100
	search?: string
	event_key?: string
	owner_kind?: EventOwnerKind | ''
	status_key?: EventRuleStatus | ''
	publish_status_key?: EventPublishStatus | ''
	has_conflict?: boolean | null
	include_archived?: boolean
	sort?: 'updated_at' | 'created_at' | 'id'
	direction?: 'asc' | 'desc'
}

export interface EventRuleWriteDto {
	event_space_id: number
	event_key: string
	conditions_operator: EventConditionGroupOperator
	conditions: EventConditionDto[]
	operation_input_id: number | null
	active_from: string | null
	active_until: string | null
	status_key: EventRuleStatus
	revision?: number
	opening_context_token?: string | null
}

export interface EventScopeWriteDto {
	kind: Exclude<EventScopeKind, 'platform'>
	entity_type?: string | null
	entity_id?: string | null
	revision?: number
	opening_context_token?: string | null
}

export interface EventScopeDraft {
	id?: number
	revision?: number
	kind: Exclude<EventScopeKind, 'platform'>
	entity_type: string | null
	entity_id: string | null
	entity?: EventEntityReferenceDto | null
}

export interface EventEntityTypeDto {
	entity_type: string
	title: string
	available: boolean
	reference_mode: 'provider' | 'technical_id'
}

export interface EntityReferenceListDto {
	items: EventEntityReferenceDto[]
	pagination: EventPaginationDto
}

export interface OperationFormContractDto {
	operation_type_key: string
	descriptor_revision: number
	input_schema: PlatformInputSchema
	context_hints: Record<string, unknown> | unknown[]
	public_form: Record<string, unknown> | null
	render_mode: 'descriptor_form' | 'owner_form'
	diagnostics: Array<{ level: string; code: string; message: string }>
}

interface OperationTypeApiDto extends OperationFormContractDto {
	title: string
	description?: string | null
	owner: { kind: 'platform' | 'instrument' | 'module'; instrument_key?: string | null; module_key?: string | null }
	available: boolean
	block_selectable: boolean
}

export interface EventPublishPreviewDto {
	publish_set: {
		rules: Array<number | Record<string, unknown>>
		scopes: Array<number | Record<string, unknown>>
		operation_inputs: number[]
	}
	dependencies: Array<{ kind?: string; id?: string | number; [key: string]: unknown }>
	impact_summary: Array<{ rule_id: number; live_scope_count: number }>
	conflicts: EventReactionConflictDto[]
	warnings: Array<{ code?: string; message?: string; [key: string]: unknown }>
	can_publish: boolean
	blocking_errors: Array<{ code?: string; message?: string; [key: string]: unknown }>
	preview_hash: string
}

export interface EventPublishResultDto {
	status: 'success'
	publish_job_id: number
	preview_hash: string
	warnings: EventPublishPreviewDto['warnings']
}

export type EventOpeningPresetMode = 'initial' | 'locked'
export interface EventOpeningPreset<T> { mode: EventOpeningPresetMode; value: T }
export interface EventReactionOpeningContext {
	event?: EventOpeningPreset<{ event_space_id: number; event_key: string }>
	conditionsOperator?: EventOpeningPreset<EventConditionGroupOperator>
	conditions?: EventOpeningPreset<EventConditionDto[]>
	operationTypeKey?: EventOpeningPreset<string | null>
	operationInputFragment?: EventOpeningPreset<Record<string, unknown>>
	scopes?: EventOpeningPreset<Array<{ kind: EventScopeKind; entity_type?: string | null; entity_id?: string | null }>>
	resolvedHostContext?: Record<string, unknown>
}

export interface EventOpeningContextTokenDto {
	opening_context_token: string
	expires_at: string
}

export interface EventApiProblem {
	message: string
	status: number
	validation?: unknown
}

export class EventApiError extends Error {
	readonly status: number
	readonly validation?: unknown

	constructor(problem: EventApiProblem) {
		super(problem.message)
		this.name = 'EventApiError'
		this.status = problem.status
		this.validation = problem.validation
	}
}

export function normalizeEventApiError(error: unknown): EventApiProblem {
	if (error instanceof EventApiError) {
		const validationMessage = firstValidationMessage(error.validation)
		const fallback = error.message || 'Не удалось выполнить запрос Event Reactions.'
		const message = validationMessage || friendlyStatusMessage(error.status, fallback)
		return { message, status: error.status, validation: error.validation }
	}
	if (error instanceof Error)
		return { message: error.message, status: 0 }
	return { message: 'Не удалось выполнить запрос Event Reactions.', status: 0 }
}

function friendlyStatusMessage(status: number, fallback: string): string {
	if (status === 403) return 'Недостаточно прав для этого действия.'
	if (status === 409) return `Данные или descriptor изменились. Обновите страницу и повторите действие. ${fallback}`
	if (status === 503) return `Владелец operation type временно недоступен. ${fallback}`
	return fallback
}

function firstValidationMessage(validation: unknown): string | null {
	if (!validation || typeof validation !== 'object') return null
	const report = validation as Record<string, unknown>
	for (const key of ['field_errors', 'form_errors']) {
		const issues = report[key]
		if (!Array.isArray(issues)) continue
		for (const issue of issues) {
			if (!issue || typeof issue !== 'object') continue
			const row = issue as Record<string, unknown>
			const message = typeof row.message === 'string' ? row.message.trim() : ''
			const path = typeof row.path === 'string' ? row.path.trim() : ''
			if (message) return path ? `${path}: ${message}` : message
		}
	}
	for (const key of ['platform', 'owner']) {
		const message = firstValidationMessage(report[key])
		if (message) return message
	}
	return null
}

export function mapOperationTypeDescriptor(item: OperationTypeApiDto): PlatformOperationTypeDescriptor {
	return {
		operationTypeKey: item.operation_type_key,
		title: item.title || item.operation_type_key,
		description: item.description ?? undefined,
		ownerKind: item.owner.kind,
		ownerInstrumentKey: item.owner.instrument_key ?? null,
		ownerModuleKey: item.owner.module_key ?? null,
		descriptorRevision: item.descriptor_revision,
		inputSchema: item.input_schema,
		contextHints: item.context_hints,
		publicForm: item.public_form,
		available: item.available,
		blockSelectable: item.block_selectable,
	}
}

export class EventsService {
	private readonly cpDomain: string

	constructor(private readonly apiService: ApiService) {
		if (!apiService)
			throw new Error('ApiService не инициализирован')
		this.cpDomain = apiService.getConfig().cp_domain
	}

	listReactions(spaceId: string | number, query: EventReactionListQuery = {}): Promise<EventReactionListDto> {
		return this.request(spaceId, `/reactions${this.query(query)}`, 'GET')
	}

	getReaction(spaceId: string | number, ruleId: number): Promise<EventReactionDetailDto> {
		return this.request(spaceId, `/reaction/${ruleId}`, 'GET')
	}

	createReaction(spaceId: string | number, draft: EventRuleWriteDto): Promise<EventRuleDto> {
		return this.request(spaceId, '/reactions', 'POST', draft)
	}

	replaceReaction(spaceId: string | number, ruleId: number, draft: EventRuleWriteDto): Promise<EventRuleDto> {
		return this.request(spaceId, `/reaction/${ruleId}`, 'PATCH', draft)
	}

	archiveReaction(spaceId: string | number, ruleId: number, revision: number, token?: string | null): Promise<EventRuleDto> {
		return this.request(spaceId, `/reaction/${ruleId}`, 'DELETE', { revision, opening_context_token: token ?? null })
	}

	createScope(spaceId: string | number, ruleId: number, scope: EventScopeWriteDto): Promise<EventScopeDto> {
		return this.request(spaceId, `/reaction-scopes/${ruleId}`, 'POST', scope)
	}

	replaceScope(spaceId: string | number, scopeId: number, scope: EventScopeWriteDto): Promise<EventScopeDto> {
		return this.request(spaceId, `/reaction-scope/${scopeId}`, 'PATCH', scope)
	}

	deleteScope(spaceId: string | number, scopeId: number, revision: number, token?: string | null): Promise<EventScopeDto> {
		return this.request(spaceId, `/reaction-scope/${scopeId}`, 'DELETE', { revision, opening_context_token: token ?? null })
	}

	listEventTypes(spaceId: string | number, search = ''): Promise<EventTypeSummaryDto[]> {
		return this.request(spaceId, `/event-types${this.query({ search })}`, 'GET')
	}

	getEventType(spaceId: string | number, eventKey: string, eventSpaceId = 0): Promise<EventTypeDetailDto> {
		return this.request(spaceId, `/event-type/${encodeURIComponent(eventKey)}${this.query({ event_space_id: eventSpaceId })}`, 'GET')
	}

	listEntityTypes(spaceId: string | number): Promise<EventEntityTypeDto[]> {
		return this.request(spaceId, '/entity-types', 'GET')
	}

	listEntityReferences(spaceId: string | number, entityType: string, search = '', page = 1): Promise<EntityReferenceListDto> {
		return this.request(
			spaceId,
			`/entity-references/${encodeURIComponent(entityType)}${this.query({ search, page, page_size: 20 })}`,
			'GET',
		)
	}

	resolveEntityReferences(spaceId: string | number, entityType: string, entityIds: Array<string | number>): Promise<EventEntityReferenceDto[]> {
		return this.request(spaceId, '/entity-reference-resolve', 'POST', { entity_type: entityType, entity_ids: entityIds })
	}

	async listOperationTypes(spaceId: string | number): Promise<PlatformOperationTypeDescriptor[]> {
		const items = await this.request<OperationTypeApiDto[]>(spaceId, '/operation-types', 'GET')
		return items.map(mapOperationTypeDescriptor)
	}

	getConflicts(spaceId: string | number, ruleId: number): Promise<EventReactionConflictDto[]> {
		return this.request(spaceId, `/reaction-conflicts/${ruleId}`, 'GET')
	}

	previewPublish(spaceId: string | number, ruleId: number): Promise<EventPublishPreviewDto> {
		return this.request(spaceId, `/reaction-publish-preview/${ruleId}`, 'POST', {})
	}

	publish(spaceId: string | number, ruleId: number, previewHash: string, token?: string | null): Promise<EventPublishResultDto> {
		return this.request(spaceId, `/reaction-publish/${ruleId}`, 'POST', {
			expected_preview_hash: previewHash,
			opening_context_token: token ?? null,
		})
	}

	createOpeningContext(spaceId: string | number, context: EventReactionOpeningContext): Promise<EventOpeningContextTokenDto> {
		return this.request(spaceId, '/reaction-opening-context', 'POST', context)
	}

	operationInputGateway(
		spaceId: string | number,
		openingContextToken: () => string | null = () => null,
	): PlatformOperationInputGateway {
		return {
			load: async (id) => this.loadOperationInput(spaceId, id),
			save: async command => {
				const id = command.input.operationInputId
				const payload = {
					operation_type_key: command.input.operationTypeKey,
					input: command.input.values,
					revision: command.input.revision ?? 0,
					descriptor_revision: command.descriptorRevision,
					context: command.openingContext ?? {},
					opening_context_token: openingContextToken(),
				}
				const result = id
					? await this.request<{ input: EventOperationInputDto }>(spaceId, `/operation-input/${id}`, 'PATCH', payload)
					: await this.request<{ input: EventOperationInputDto }>(spaceId, '/operation-input', 'POST', payload)
				return this.operationInputState(result.input)
			},
			discardCreated: async id => {
				const current = await this.loadOperationInput(spaceId, id)
				await this.request(spaceId, `/operation-input/${id}`, 'DELETE', {
					revision: current.revision ?? 0,
					opening_context_token: openingContextToken(),
				})
			},
		}
	}

	private async loadOperationInput(spaceId: string | number, id: number): Promise<PlatformOperationInputState> {
		const result = await this.request<{ input: EventOperationInputDto }>(spaceId, `/operation-input/${id}`, 'GET')
		return this.operationInputState(result.input)
	}

	private operationInputState(input: EventOperationInputDto): PlatformOperationInputState {
		return {
			operationInputId: input.id,
			operationTypeKey: input.operation_type_key,
			values: { ...input.input },
			revision: input.revision,
		}
	}

	private async request<T>(spaceId: string | number, path: string, method: string, body?: unknown): Promise<T> {
		try {
			const response = await this.apiService.doFetch(
				this.cpDomain,
				`/${spaceId}/events/s${path}`,
				method,
				body,
			)
			const result = await this.apiService.getResponseJson(response)
			if (!result.success) {
				const errorData = typeof result.error === 'object' && result.error !== null
					? result.error as unknown as { error?: string; data?: { validation?: unknown } }
					: null
				throw new EventApiError({
					message: errorData?.error || String(result.error || 'Ошибка Event Reactions API'),
					status: result.response_code ?? response.status,
					validation: errorData?.data?.validation ?? (result.data as { validation?: unknown } | undefined)?.validation,
				})
			}
			return result.data as T
		} catch (error) {
			if (error instanceof EventApiError)
				throw error
			throw new EventApiError(normalizeEventApiError(error))
		}
	}

	private query(values: object): string {
		const params = new URLSearchParams()
		for (const [key, value] of Object.entries(values)) {
			if (value === undefined || value === null || value === '')
				continue
			params.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value))
		}
		const query = params.toString()
		return query ? `?${query}` : ''
	}
}

export type EventReactionGateway = EventsService
